import Modal from "@/components/common/modal";
import { DialogFooter } from "@/components/ui/dialog";
import { Button, TextAreaField } from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import SelectField from "@/components/common/select";

const resolutionSchema = z.object({
  veredict: z.string().min(1, "Selecciona un veredicto"),
  comment: z
    .string()
    .min(5, "El comentario debe tener al menos 5 caracteres")
    .max(255, "El comentario no puede exceder 255 caracteres"),
});

type ResolutionFormData = z.infer<typeof resolutionSchema>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { veredict: string; comment: string }) => void;
  isLoading?: boolean;
  hasErrorFlows?: boolean; // Si hay flujos con error, solo permitir Rechazar
}

const SendResolutionModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  hasErrorFlows = false,
}: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<ResolutionFormData>({
    resolver: zodResolver(resolutionSchema),
    defaultValues: {
      veredict: hasErrorFlows ? "DENIED" : undefined,
      comment: "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const comment = watch("comment");
  const verdict = watch("veredict");

  const handleClose = () => {
    reset({
      veredict: undefined,
      comment: "",
    });
    onClose();
  };

  const verdictOptions = hasErrorFlows
    ? [{ label: "Rechazado", value: "DENIED" }]
    : [
        { label: "Aprobado", value: "APPROVED" },
        { label: "Rechazado", value: "DENIED" },
      ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enviar resolución"
      description="Envía el veredicto final para esta versión del proceso."
      modalContentClassName="max-w-[520px] w-full gap-7"
    >
      <form
        className="flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
        id="send-resolution-form"
      >
        <SelectField
          label="Veredicto"
          name="verdict"
          required
          placeholder="Selecciona un veredicto"
          options={verdictOptions}
          value={verdict}
          onChange={(value) => {
            setValue("veredict", value as "APPROVED" | "DENIED", {
              shouldValidate: true,
            });
          }}
          error={errors.veredict?.message}
          isDisabled={hasErrorFlows}
        />

        {hasErrorFlows && (
          <p className="text-sm text-warning">
            Existen flujos con errores de validación. Solo puedes rechazar esta
            versión.
          </p>
        )}

        <TextAreaField
          label="Comentario"
          required
          maxLength={255}
          placeholder="Describe el motivo de tu decisión"
          rows={3}
          className="resize-none"
          error={errors.comment?.message}
          {...register("comment")}
          count={comment?.length || 0}
          limit={255}
        />

        <DialogFooter className="pt-1">
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
            <Button
              size="lg"
              type="button"
              className="w-full"
              variant="tertiary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              size="lg"
              className="w-full"
              type="submit"
              disabled={isLoading || !isValid}
              isLoading={isLoading}
            >
              Enviar resolución
            </Button>
          </div>
        </DialogFooter>
      </form>
    </Modal>
  );
};

export default SendResolutionModal;
